import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import LgcSharedPlanView from '../components/financial/LgcSharedPlanView';
import { FinancialPlanSharePayload, FinancialPlanShareView } from '../types/financialPlanShare';
import { sharePayloadToView } from '../utils/computeSharedPlanView';
import {
  getFinancialPlanShareMeta,
  pollFinancialPlanShare,
  readStoredSharePin,
  storeSharePin,
  verifyFinancialPlanShare,
} from '../utils/financialPlanShareService';
import { sharedPlanIntroMessage, sharedPlanLoadingMessage } from '../utils/financialPlanShareCopy';
import './SharedFinancialPlanPage.css';

const POLL_INTERVAL_MS = 12_000;

const SharedFinancialPlanPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planName, setPlanName] = useState('');
  const [ownerDisplayName, setOwnerDisplayName] = useState<string | null>(null);
  const [view, setView] = useState<FinancialPlanShareView | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [liveRevision, setLiveRevision] = useState(0);
  const [requiresPin, setRequiresPin] = useState(false);
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState(false);
  const [verifyingPin, setVerifyingPin] = useState(false);
  const pinInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const pinRef = useRef<string | null>(null);

  const applyPayload = useCallback((payload: FinancialPlanSharePayload & { unchanged?: boolean }) => {
    if (payload.unchanged) return;
    try {
      const nextView = sharePayloadToView(payload);
      setView(nextView);
      setPlanName(nextView.planName);
      setUpdatedAt(payload.updatedAt);
      setLiveRevision(payload.liveRevision);
    } catch (err) {
      console.error('sharePayloadToView failed', err);
    }
  }, []);

  const tryAutoLogin = useCallback(async () => {
    if (!slug) return false;
    const storedPin = readStoredSharePin(slug);
    if (!storedPin) return false;
    try {
      const payload = await verifyFinancialPlanShare(slug, storedPin);
      pinRef.current = storedPin;
      applyPayload(payload);
      setRequiresPin(false);
      return true;
    } catch {
      return false;
    }
  }, [slug, applyPayload]);

  useEffect(() => {
    if (!slug) {
      setError('Invalid share link.');
      setLoading(false);
      return;
    }

    const run = async () => {
      try {
        const meta = await getFinancialPlanShareMeta(slug);
        setPlanName(meta.planName);
        setOwnerDisplayName(meta.ownerDisplayName);
        const ok = await tryAutoLogin();
        if (!ok) {
          setRequiresPin(true);
        }
      } catch (err) {
        console.error('getFinancialPlanShareMeta failed', err);
        setError('This share link is invalid or has been revoked.');
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [slug, tryAutoLogin]);

  useEffect(() => {
    if (!slug || !pinRef.current || requiresPin || !view) return;

    const poll = async () => {
      const pin = pinRef.current;
      if (!pin) return;
      try {
        const payload = await pollFinancialPlanShare(slug, pin, liveRevision);
        applyPayload(payload);
      } catch (err) {
        console.warn('pollFinancialPlanShare failed', err);
      }
    };

    const id = window.setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [slug, requiresPin, view, liveRevision, applyPayload]);

  const handlePinChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...pinDigits];
    next[index] = digit;
    setPinDigits(next);
    setPinError(false);
    if (digit && index < 3) {
      pinInputRefs.current[index + 1]?.focus();
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      pinInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && pinDigits.every(Boolean)) {
      void verifyPin();
    }
  };

  const handlePinPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!pasted) return;
    const next = pasted.split('').concat(['', '', '', '']).slice(0, 4);
    setPinDigits(next);
    pinInputRefs.current[Math.min(pasted.length, 3)]?.focus();
  };

  const verifyPin = async () => {
    if (!slug || pinDigits.some((d) => !d)) return;
    const pin = pinDigits.join('');
    setVerifyingPin(true);
    setPinError(false);
    try {
      const payload = await verifyFinancialPlanShare(slug, pin);
      pinRef.current = pin;
      storeSharePin(slug, pin);
      applyPayload(payload);
      setRequiresPin(false);
    } catch (err) {
      console.error('verifyFinancialPlanShare failed', err);
      setPinError(true);
      setPinDigits(['', '', '', '']);
      pinInputRefs.current[0]?.focus();
    } finally {
      setVerifyingPin(false);
    }
  };

  if (loading) {
    return (
      <div className="lgc-shared-page">
        <p className="lgc-shared-page__status">
          <Loader2 size={18} className="lgc-shared-page__spin" aria-hidden />
          {sharedPlanLoadingMessage(ownerDisplayName)}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lgc-shared-page">
        <div className="lgc-shared-page__card lgc-shared-page__card--error">
          <h1>Plan not available</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (requiresPin && !view) {
    return (
      <div className="lgc-shared-page">
        <div className="lgc-shared-page__card">
          <div className="lgc-shared-page__lock" aria-hidden>
            <Lock size={28} />
          </div>
          <p className="lgc-shared-page__intro">{sharedPlanIntroMessage(ownerDisplayName)}</p>
          <h1>{planName || 'Financial plan'}</h1>
          <p>Enter the 4-digit PIN to view this live shared plan.</p>

          <div className="lgc-shared-page__pin-inputs" onPaste={handlePinPaste}>
            {pinDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  pinInputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handlePinKeyDown(index, e)}
                className={`lgc-shared-page__pin-input${digit ? ' filled' : ''}`}
                autoFocus={index === 0}
                aria-label={`PIN digit ${index + 1}`}
              />
            ))}
          </div>

          {pinError && <p className="lgc-shared-page__pin-error">Incorrect PIN. Try again.</p>}

          <button
            type="button"
            className="lgc-shared-page__pin-btn"
            onClick={() => void verifyPin()}
            disabled={verifyingPin || pinDigits.some((d) => !d)}
          >
            {verifyingPin ? (
              <>
                <Loader2 size={16} className="lgc-shared-page__spin" aria-hidden />
                Verifying…
              </>
            ) : (
              'View plan'
            )}
          </button>
        </div>
      </div>
    );
  }

  if (!view) {
    return null;
  }

  return (
    <div className="lgc-shared-page lgc-shared-page--full">
      <LgcSharedPlanView
        view={view}
        updatedAt={updatedAt}
        ownerDisplayName={ownerDisplayName}
        live
      />
    </div>
  );
};

export default SharedFinancialPlanPage;
